/**
 * The mandatory first password change.
 *
 * Where an organization owner lands after signing in with the credentials a
 * platform administrator issued them, and where a new member lands after
 * signing in with the ones their owner issued.
 *
 * Nothing on this screen is optional. The token that got the user here is
 * scoped to the password-change endpoints, so the ERP is genuinely closed to
 * them — not merely hidden — until this succeeds. On success the API returns a
 * fresh, unrestricted token pair, so they continue straight to the dashboard
 * rather than signing in a second time.
 *
 * Signing out is the only other way off this page, which is deliberate: the
 * alternative is a user stuck with no exit if they mistyped their email.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, ErrorAlert, PasswordInput } from '../../components/index.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../context/ToastContext.jsx';

/**
 * The same policy the API enforces, so it fails here instead of on submit.
 * Returns keys under auth.password.issues, translated where they are shown.
 */
function passwordIssues(password) {
  const issues = [];
  if (password.length < 8) issues.push('length');
  if (!/[a-z]/.test(password)) issues.push('lowercase');
  if (!/[A-Z]/.test(password)) issues.push('uppercase');
  if (!/[0-9]/.test(password)) issues.push('number');
  return issues;
}

export function ChangePasswordPage() {
  const { t } = useTranslation('administration');
  const { user, completePasswordChange, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });

  const issues = useMemo(() => passwordIssues(form.newPassword), [form.newPassword]);
  const mismatch = form.confirm.length > 0 && form.newPassword !== form.confirm;
  const sameAsTemporary = form.newPassword.length > 0 && form.newPassword === form.currentPassword;

  const ready =
    form.currentPassword.length > 0 && issues.length === 0 && !mismatch && !sameAsTemporary && form.confirm.length > 0;

  const change = useMutation(async () => {
    await completePasswordChange({
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
    });

    toast.success(t('auth.change.success'));
    // AuthContext has already reloaded the session with an unrestricted
    // token, so the shell will let them through.
    navigate('/', { replace: true });
  });

  /**
   * The two ways the API can reject this, mapped onto the field at fault.
   *
   * Both arrive as a plain message on the error rather than as field details,
   * so without this they would only ever appear in the banner above the form -
   * away from the input the user has to correct.
   */
  const wrongCurrentPassword = change.error?.code === 'INVALID_CREDENTIALS';
  const newPasswordReused = change.error?.status === 409;

  /**
   * Edits the form and drops any message from the previous attempt.
   *
   * "That is not the temporary password you were sent" should not still be
   * sitting under a field the user has already started correcting — an error
   * about text that is no longer there reads as the app being stuck.
   */
  const update = (patch) => {
    if (change.error) change.setError(null);
    setForm((current) => ({ ...current, ...patch }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    change.mutate().catch(() => {
      // Rendered by ErrorAlert below.
    });
  };

  const stillNeeds = () =>
    t('auth.password.stillNeeds', {
      list: issues
        .map((issue) => t(`auth.password.issues.${issue}`, { bilingual: false }))
        .join(t('auth.password.listSeparator', { bilingual: false })),
    });

  return (
    <Card>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <div>
          <h1 className="auth-title">{t('auth.change.title')}</h1>
          <p className="auth-subtitle">
            {user?.fullName
              ? t('auth.change.welcomeNamed', { name: user.fullName })
              : t('auth.change.welcome')}
          </p>
        </div>

        <ErrorAlert error={change.error} />

        <Alert variant="info" title={t('auth.change.oneOffTitle')}>
          {t('auth.change.oneOffMessage')}
        </Alert>

        <PasswordInput
          label={t('auth.change.temporary')}
          name="currentPassword"
          autoComplete="current-password"
          required
          autoFocus
          hint={t('auth.change.temporaryHint')}
          value={form.currentPassword}
          error={
            change.fieldErrors.currentPassword ??
            (wrongCurrentPassword ? t('auth.change.wrongTemporary') : undefined)
          }
          onChange={(event) => update({ currentPassword: event.target.value })}
        />

        <PasswordInput
          label={t('auth.password.new')}
          name="newPassword"
          autoComplete="new-password"
          required
          value={form.newPassword}
          error={
            change.fieldErrors.newPassword ??
            (sameAsTemporary || newPasswordReused ? t('auth.change.sameAsTemporary') : undefined)
          }
          hint={form.newPassword && issues.length ? stillNeeds() : t('auth.password.rules')}
          onChange={(event) => update({ newPassword: event.target.value })}
        />

        <PasswordInput
          label={t('auth.password.confirm')}
          name="confirm"
          autoComplete="new-password"
          required
          value={form.confirm}
          error={mismatch ? t('auth.password.mismatch') : undefined}
          onChange={(event) => update({ confirm: event.target.value })}
        />

        <Button type="submit" variant="primary" size="lg" block loading={change.pending} disabled={!ready}>
          {t('auth.change.submit')}
        </Button>

        <p className="auth-footer">
          {t('auth.change.signedInAs', { email: user?.email ?? '' })}{' '}
          <button type="button" className="btn-link" onClick={signOut}>
            {t('auth.change.signOut')}
          </button>
        </p>
      </form>
    </Card>
  );
}
